import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { catchError, takeUntil, debounceTime, filter, switchMap, skip } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { LoggerService } from '@wdpr/ra-angular-logger';
import get from 'lodash-es/get';

// Components
import { CalendarFiltersComponent } from './components/filters/calendar-filters.component';
import { DatePickerModalComponent } from '@finder/features/calendars/components/date-picker-modal/date-picker-modal.component';
import { MobileFiltersComponent } from '@finder/features/calendars/components/mobile-filters/mobile-filters.component';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ViewAllModalComponent } from './components/view-all/view-all.component';
// Interfaces
import { Location } from '@finder/features/calendars/calendars.interface';
// Services
import { CalendarsService } from './services/calendars.service';
import { ConfigService } from '@finder/core/config.service';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { DebugService } from '@finder/features/debug/services/debug-service/debug.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { FiltersHashService } from '@finder/features/list/services/filters-hash/filters-hash.service';
import { FinderSeoService } from '@finder/shared/services/finder-seo/finder-seo.service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { UserSelectionStorage } from '@finder/shared/services/user-selection-storage/user-selection-storage.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
// Others
import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { GetDateStringProvider, GET_DATE_STRING } from '@finder/shared/utils/string/getDateString';
import {
    GetSimpleDateFromDateObjectProvider,
    GET_SIMPLE_DATE_FROM_DATE_OBJECT
} from '@finder/shared/utils/date/getSimpleDateFromDateObject';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { GetYMDProvider, GET_YMD } from '@finder/shared/utils/string/getYMD';
import { DateRange } from '@finder/features/calendars/calendars.interface';
import { SITE_ID } from '@finder/shared/constants/app.constants';
import { CALENDAR_CONSTANTS, CONFIG_CONSTANTS } from '@finder/shared/constants/app.constants';
import { CommonModule } from '@angular/common';
import { TitleComponent } from '@finder/shared/components/title/title.component';
import { SwitchButtonComponent } from '@finder/shared/components/switch-button/switch-button.component';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { BreadcrumbComponent } from '@finder/shared/components/breadcrumb/breadcrumb.component';
import { StickyBarComponent } from './components/sticky-bar/sticky-bar.component';
import { DatePickerWrapperComponent } from '@finder/shared/components/date-picker-wrapper/date-picker-wrapper.component';
import { IntersectionDirective } from '@finder/shared/directives/intersection/intersection.directive';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';

@Component({
    selector: 'finder-calendars',
    templateUrl: './calendars.component.html',
    styleUrls: ['./calendars.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TitleComponent,
        SwitchButtonComponent,
        AnchorComponent,
        CalendarFiltersComponent,
        BreadcrumbComponent,
        StickyBarComponent,
        DatePickerWrapperComponent,
        IntersectionDirective,
        SafeHtmlPipe,
        RouterModule,
    ],
    providers: [
        DebugPanelService,
        DebugService,
        FilterEmitterService,
        FiltersHashService,
        FinderSeoService,
        LocaleHelperService,
        LocaleService,
        LoggerService,
        ModalService,
        UserSelectionStorage,
    ]
})
export class CalendarsComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(CalendarFiltersComponent) filters: CalendarFiltersComponent;

    appliedFilters: Facet[] = [];
    calendarData: any;
    datePickerModal: ModalComponent;
    datePickerText: string;
    debug = false;
    defaultView = true;
    disclaimer: any;
    fiveDay = false;
    fullDateString: string;
    isDesktop = false;
    isMobile = false;
    labels: any;
    sponsorshipCopy: Array<string>;
    sponsorShipLogoUrl: string;
    links: any;
    locations: Array<Location>;
    mobileFilters: ModalComponent;
    selectedDate: Date = new Date();
    shortDateString: string;
    siteId: string;
    stickyBar = false;
    switchButtonLinks: Array<string>;
    updateCalendarData$ = new Subject<any>();
    viewAllModal: ModalComponent;
    viewType: string;

    private destroy$ = new Subject<void>();
    private locale: string;

    constructor(
        private calendarsService: CalendarsService,
        private configService: ConfigService,
        private debugPanelService: DebugPanelService,
        private debugService: DebugService,
        private filterEmitterService: FilterEmitterService,
        private filtersHash: FiltersHashService,
        private finderAnalyticsService: FinderAnalyticsService,
        private finderApi: FinderApiConnectorService,
        private finderSeoService: FinderSeoService,
        private localeHelper: LocaleHelperService,
        private localeService: LocaleService,
        private logger: LoggerService,
        private modalService: ModalService,
        private route: ActivatedRoute,
        private router: Router,
        private userSelection: UserSelectionStorage,
        private viewContainerRef: ViewContainerRef,
        private viewportService: ViewportDetectionService,
        @Inject(GET_DATE_STRING) private getDateString: GetDateStringProvider,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
        @Inject(GET_SIMPLE_DATE_FROM_DATE_OBJECT) private getSimpleDateFromDateObject: GetSimpleDateFromDateObjectProvider,
        @Inject(GET_YMD) private getYMD: GetYMDProvider,
    ) { }

    ngOnInit() {
        this.debug = this.debugService.isDebugEnabled();
        this.isMobile = this.viewportService.isMobile();
        this.isDesktop = this.viewportService.isDesktop();
        this.calendarData = get(this.route.snapshot.data, 'calendar');
        this.siteId = this.configService.getValue(CONFIG_CONSTANTS.siteIdKey);
        this.locale = this.localeHelper.getNormalizedLocale(this.localeService.getLocale());

        if (!this.calendarData) {
            this.logger.error('Calendar SVC -- No service response');

            return;
        }

        // Pull the locations and the viewType from the response.
        this.locations = this.calendarData.locations;
        this.viewType = this.calendarData.viewType;
        this.labels = this.calendarData.labels;

        this.filterCalendarLinks();

        // Check Five Day view.
        this.checkFiveDayView();
        this.listenForUpdates();
        // Set Selected Date and Filters from Local Storage.
        this.setSelectedDate();
        this.setSelectedFilters();
        this.setDisclaimer();
        this.emitLocations();

        this.switchButtonLinks = this.calendarData.switchButton?.links;

        // We add the default path to the links. Could be:
        //  /calendars
        //  /park-calendars
        //  /reservations/{store}/calendars
        this.switchButtonLinks?.forEach(buttonLink => {
            buttonLink['href'] = `${this.calendarsService.getCalendarsDefaultPath()}${buttonLink['href']}`;
        });

        this.sponsorshipCopy = this.labels?.sponsorship?.split('{sponsor-logo}');
        this.sponsorShipLogoUrl = this.calendarData?.sponsorshipLogo?.logoUrl;

        // Subscribe to Filter updates.
        this.filterEmitterService.watchFilterList()
            .pipe(
                debounceTime(50),
                takeUntil(this.destroy$)
            )
            .subscribe((filterList) => this.onWatchFacetList(filterList));

        // Subscribe to Date Picker updates (Mobile).
        this.calendarsService.watchDate()
            .pipe(
                filter((date) => date instanceof Date),
                takeUntil(this.destroy$)
            )
            .subscribe(date => this.onDateChanged(date));

        this.calendarsService.watchViewAll()
            .pipe(
                filter((data) => data),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.openViewAllModal());

        this.setUpModals();
        this.finderAnalyticsService.initAnalyticsLibrary(this.calendarData.analytics);
        this.finderAnalyticsService.getGuestModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe(guestModel => {
                // Update the analytics model
                this.finderAnalyticsService.updateAnalyticsModel(
                    null,
                    guestModel,
                    '',
                    '',
                    '',
                    {linkId: `${this.siteId}_calendar_${this.viewType}`}
                );
            });
    }

    // Expose the calendar service date object to the template instead of setting a local var that will get out of sync
    get dateRange(): DateRange {
        return this.calendarsService.dateRange;
    }

    ngAfterViewInit() {
        this.debugPanelService.createDebugPanel(this.viewContainerRef, this.calendarData);

        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobile = this.viewportService.isMobile();
                this.isDesktop = this.viewportService.isDesktop();

                this.checkFiveDayView();

                // Close Modals on Tablet/Desktop view.
                /* istanbul ignore else */
                if (!this.isMobile) {
                    this.modalService.dismissAllModals();
                }
            });

        //subscribe to a route's fragment change (route#fragment = calendars/day/#/EPCOT)
        this.route.fragment
        .pipe(
            skip(1), // skip the first change when the page loads
            takeUntil(this.destroy$))
        .subscribe(() => {
            this.setSelectedFilters();
            this.emitFilters();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async onViewChanged(viewType) {
        this.viewType = viewType;
        this.checkFiveDayView();
        this.updateCalendarData$.next(this.viewType);

        // Set SEO
        this.finderSeoService.getPageKeyAndSetSeo();

        await this.finderAnalyticsService.updateOtherProperties(
            {linkId: `${this.siteId}_calendar_${this.viewType}`,
                pageId: this.viewType});

        // Get date with format YYYY_MM_DD.
        const dateYMDFormat = this.getYMD(this.selectedDate);
        this.router.navigate([this.calendarsService.getCalendarsDefaultPath(), this.viewType, dateYMDFormat]);
    }

    onWatchFacetList(filterList: Facet[]) {
        // Reverse the applied filters, so the last we added
        // appears at the top, by de-structuring and creating a new array,
        // so reverse doesn't impact filterList
        this.appliedFilters = [...filterList].reverse();
        this.defaultView = filterList.length === 0;

        // Store selected filters as a comma-separated string.
        const filters = filterList.map(f => f.id).join(',');
        this.userSelection.saveCalendarFilters(filters);

        this.emitLocations();
    }

    onDateChanged(date) {
        this.selectedDate = date;
        this.fullDateString = this.getFullDateString(this.selectedDate, this.locale);
        const simpleDate = this.getSimpleDateFromDateObject(date, this.locale);
        // Store selected date in Session Storage.
        this.userSelection.saveCalendarDate(simpleDate);
        // Update Date Range
        this.calendarsService.setDateRange(date);

        // Update Calendar Locations
        this.updateCalendarData$.next(this.viewType);
        // Update Short Date string
        this.shortDateString = this.getDateString(this.selectedDate, this.locale);

        // Fire analytics with new selected date info
        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `calendar_${this.viewType}_selectedDate_${simpleDate}`,
            bookingSearchArrivalDate: simpleDate,
        });

        // Get date with format YYYY_MM_DD.
        const dateYMDFormat = this.getYMD(this.selectedDate);
        this.router.navigate([this.calendarsService.getCalendarsDefaultPath(), this.viewType, dateYMDFormat]);
    }

    listenForUpdates() {
        this.updateCalendarData$.pipe(
            debounceTime(50),
            switchMap(viewType => {
                const formattedDate = this.getYMD(this.selectedDate);
                this.calendarsService.setLoadingState(true);

                return this.finderApi.getCalendarData(viewType, formattedDate);
            }),
            catchError((error) => {
                this.logger.error(`Error while getting the Calendar Data: ${error.message}`);

                return of(false);
            }),
            takeUntil(this.destroy$)
        )
        .subscribe(locationsData => {
            if (!locationsData) {
                return;
            }
            this.locations = locationsData.locations;
            this.emitLocations();
            this.calendarsService.setLoadingState(false);
        });
    }

    setUpModals() {
        this.datePickerModal = this.modalService.create(
            DatePickerModalComponent,
            {
                id: 'date-picker-modal',
                hideOnClose: true,
                title: this.labels.datePickerText,
                data: {
                    labels: this.labels
                }
            }
        );
        this.mobileFilters = this.modalService.create(
            MobileFiltersComponent,
            {
                id: 'mobile-filters-modal',
                hideOnClose: true,
                title: this.labels.mobileFilters.title,
                data: {
                    labels: this.labels,
                    filters: get(this.calendarData, 'filters', {})
                }
            }
        );
        this.viewAllModal = this.modalService.create(
            ViewAllModalComponent,
            {
                id: 'view-all-modal',
                hideOnClose: true,
                title: this.labels.schedules
            }
        );
    }

    openDatePickerModal() {
        this.datePickerModal.open();
    }

    openMobileFilters() {
        this.mobileFilters.open();
    }

    openViewAllModal() {
        this.viewAllModal.open();
    }

    showStickyBar(state: boolean) {
        this.stickyBar = state;
    }

    filterCalendarLinks() {
        // Trade and park-calendars pages should not show any links, so we
        // are checking the actual path to know if we are allowed to show them
        if (!this.calendarsService.allowLinksOnPage()) {
            return;
        }

        // assign links to object
        this.links = this.calendarData.links;

        // Filter Magic Access links for some locales
        if (this.siteId === SITE_ID.hkdl && !CALENDAR_CONSTANTS.hkdlMagicAccessLocales.includes(this.locale)) {
            delete this.links['annualPass'];
        }
    }

    checkFiveDayView() {
        this.fiveDay = this.viewType === 'five-day';
        this.datePickerText = this.fiveDay ? this.labels.datePickerTextPlural : this.labels.datePickerText;
        // The Tablet/Mobile view for the five-day view is not allowed.
        // Change to the day view instead.
        if (!this.isDesktop && this.fiveDay) {
            // Run onViewChanged hook to update park locations.
            this.onViewChanged('day');

            // Get date with format YYYY_MM_DD.
            const dateYMDFormat = this.getYMD(this.selectedDate);
            const calendarPath = `${this.calendarsService.getCalendarsDefaultPath()}/day`;
            // Render Day view component.
            this.router.navigate([calendarPath, dateYMDFormat]);
        }
    }

    setDisclaimer() {
        const disclaimerLink = get(this.links, 'disclaimer');
        if (!disclaimerLink) {
            return;
        }

        this.disclaimer = {
            breadcrumbTemplate: this.labels.disclaimer,
            links: [{
                label: this.labels.disclaimerLabel,
                link: disclaimerLink
            }]
        };
    }

    private setSelectedDate() {
        this.shortDateString = this.getDateString(this.selectedDate, this.locale);
        // Get stored date from Session Storage or default to today.
        this.selectedDate = this.userSelection.getCalendarDate();
        // Format the selectedDate to display it in the calendar view.
        this.fullDateString = this.getFullDateString(this.selectedDate, this.locale);
    }

    private setSelectedFilters() {
        const topFilters = get(this.calendarData, 'filters.topFilters', []);

        // First we check if there's any filter applied on the URL
        const hashElements = this.filtersHash.checkHash(topFilters);

        if (hashElements.facets) {
            const hashElementsFacets = hashElements.facets.map(facet => facet.id);

            if (hashElementsFacets.length) {
                // If we have hash values, we take those instead of getting Session Storage ones.
                topFilters.forEach(topFilter => topFilter.preSelected = hashElementsFacets.includes(topFilter.id));

                return;
            }
        }

        // If not, we get stored filters from Session Storage and apply them if any.
        const storedFilters = this.userSelection.getCalendarFilters();
        if (!storedFilters) {
            return;
        }

        const appliedFilters = storedFilters.split(',');

        topFilters.forEach(topFilter => topFilter.preSelected = appliedFilters.includes(topFilter.id));
    }

    private emitLocations() {
        this.calendarsService.emitLocations(
            this.locations,
            this.appliedFilters,
            this.selectedDate
        );
    }

    private emitFilters() {
        /* istanbul ignore if */
        if (!this.calendarData?.filters?.topFilters) {
            return;
        }
        this.calendarData.filters.topFilters.forEach((topFilter) => {
            this.filterEmitterService.emitFilter(
                new Facet(topFilter.id, topFilter.label, topFilter.preSelected, topFilter.group)
            );
        });
    }
}
